/**
 * W3 — Casing & tubular design. Authors the casing strings of a plan version:
 * string × type × section × hole size × casing OD × top/base installation depth ×
 * length, for drilling / sidetrack / completion / workover events (whichever carry
 * a casing dataset — see the §16b section matrix). An editable grid (shared
 * InlineRowGrid) on the left, a concentric hand-SVG casing schematic on the right;
 * selecting a row highlights its string in the schematic and vice-versa. The
 * casing OD cell binds to the Oxbo-M tubular catalog via CatalogPicker so the
 * planner picks a real OD rather than inventing one. Reads/writes the pApi casing
 * dataset endpoint (MSW-seeded standalone).
 *
 * Built to w3-casing-design.html on the P3 kit. Nesting validation
 * (validateNesting) flags a string whose OD can't fit its own hole or doesn't
 * nest inside the string above — surfaced inline per row and in a note strip.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import {
  useGetEventPlansQuery,
  useGetPlanDatasetsQuery,
  usePutCasingMutation,
} from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { QueryState } from "@components/ui/QueryState";
import { InlineRowGrid, type GridColumn } from "@components/input/InlineRowGrid";
import { CatalogPicker } from "@components/catalog/CatalogPicker";
import type { CasingRow, PlanOut } from "@api/types";
import { enrichEventCode, sectionApplies } from "@lib/eventEnrichment";
import { asArray } from "@lib/asArray";
import { fmtNum } from "@lib/format";
import { latestPlan, statusClass, STATUS_KEY } from "@features/planFinder/planCatalog";
import { CasingSchematic } from "./casingSchematic";
import {
  CASING_TYPES,
  casingTotals,
  computedLength,
  nextSeq,
  typeVar,
  validateNesting,
} from "./casingModel";
import "./casing.css";

/** Stable per-row key for grid ↔ schematic selection (seq is stable enough). */
const rowKey = (r: CasingRow, i: number) => `str-${r.seq || i}-${i}`;

function makeEmptyRow(rows: CasingRow[]): CasingRow {
  return {
    seq: nextSeq(rows),
    string_name: "",
    type: "",
    hole_size: null,
    casing_size: null,
    section_number: null,
    top_installation: 0,
    base_installation_depth: null,
    installation_length: null,
  };
}

export function Casing() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();

  // ── Resolve plan context (event → requested/latest plan) ───────────────────
  const activeEventId = useAppSelector((s) => s.wellContext.activeEventId);
  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);

  const { data: plansRaw, isLoading: plansLoading } = useGetEventPlansQuery(activeEventId ?? "", {
    skip: !activeEventId,
  });
  // asArray guards a present-but-non-array body before .find / latestPlan's spread.
  const resolvedPlan = useMemo(() => {
    const plans = asArray<PlanOut>(plansRaw);
    if (activePlanId) return plans.find((p) => p.id === activePlanId) ?? latestPlan(plans);
    return latestPlan(plans);
  }, [activePlanId, plansRaw]);

  const planId = resolvedPlan?.id ?? null;
  const enrich = enrichEventCode(resolvedPlan?.event_code);
  const casingApplies = sectionApplies("casing", enrich.letter, enrich.isConventional);

  // ── Datasets (the casing strings) ──────────────────────────────────────────
  const {
    data: datasets,
    isLoading: dsLoading,
    isError: dsError,
    refetch,
  } = useGetPlanDatasetsQuery(planId ?? "", { skip: !planId });
  const [putCasing, { isLoading: saving }] = usePutCasingMutation();

  // Tubular catalog (Oxbo-M) — drives the casing OD picker.
  const { data: tubulars } = useGetResolvedCatalogQuery({ categoryKey: "tubulars" });
  // asArray: a non-array catalog body must not crash the OD-lookup .find below.
  const tubularEntries = asArray<{ key: string; label: string; meta?: Record<string, unknown> }>(
    tubulars?.entries,
  );

  // Local editable copy of the rows (committed to the server on Save).
  const [rows, setRows] = useState<CasingRow[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    // A real backend can return a present-but-non-array body ({}/{casing:null});
    // asArray guards the .map so it never white-screens (see src/lib/asArray.ts).
    if (datasets) {
      setRows(asArray<CasingRow>(datasets.casing).map((r) => ({ ...r })));
      setDirty(false);
    }
  }, [datasets]);

  // Keep length auto-derived whenever top/base change; renumber seq on reorder.
  function applyRows(next: CasingRow[]) {
    const normalized = next.map((r, i) => ({
      ...r,
      seq: i + 1,
      installation_length: computedLength(r),
    }));
    setRows(normalized);
    setDirty(true);
  }

  async function save() {
    if (!planId) return;
    await putCasing({ planId, rows }).unwrap();
    setDirty(false);
    setSavedAt(new Date().toLocaleTimeString(locale === "en" ? "en-US" : "es-AR"));
  }

  // ── Nesting validation + totals ────────────────────────────────────────────
  const nesting = useMemo(() => validateNesting(rows), [rows]);
  const totals = useMemo(() => casingTotals(rows, nesting), [rows, nesting]);
  // The first flagged row, for the note strip's example.
  const firstWarnIdx = nesting.byRow.findIndex((f) => f.length > 0);

  /** OD label for a tubular catalog key (meta.od → inches), for the picker cell. */
  function odForTubular(key: string | null): number | null {
    if (!key) return null;
    const meta = tubularEntries.find((e) => e.key === key)?.meta;
    const od = meta?.["od"];
    return typeof od === "number" ? od : null;
  }

  // ── Grid columns ────────────────────────────────────────────────────────────
  const columns: GridColumn<CasingRow>[] = [
    {
      key: "string_name",
      label: t("casing.col.string"),
      render: (row, _update, i) => {
        const key = rowKey(row, i);
        return (
          <button
            type="button"
            className="cs-strname"
            title={t("casing.selectHint")}
            onClick={() => setSelectedKey(key)}
          >
            <span className="cs-sw" style={{ background: `var(${typeVar(row.type)})` }} />
            {row.string_name || <span className="dim">{t("casing.unnamed")}</span>}
          </button>
        );
      },
    },
    {
      key: "type",
      label: t("casing.col.type"),
      render: (row, update) => (
        <select
          className="cs-cell"
          value={row.type || ""}
          aria-label={t("casing.col.type")}
          onChange={(e) => update({ type: e.target.value })}
        >
          <option value="">{t("casing.type.choose")}</option>
          {CASING_TYPES.map((ty) => (
            <option key={ty} value={ty}>
              {t(`casing.type.${ty}`)}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "section_number",
      label: t("casing.col.section"),
      width: 70,
      render: (row, update) => (
        <input
          className="cs-cell"
          style={{ minWidth: 48 }}
          value={row.section_number ?? ""}
          aria-label={t("casing.col.section")}
          onChange={(e) => update({ section_number: e.target.value || null })}
        />
      ),
    },
    {
      key: "hole_size",
      label: t("casing.col.hole"),
      numeric: true,
      render: (row, update) => (
        <input
          className="cs-cell num tab-nums"
          inputMode="decimal"
          value={row.hole_size ?? ""}
          aria-label={t("casing.col.hole")}
          onChange={(e) =>
            update({ hole_size: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "casing_size",
      label: t("casing.col.od"),
      width: 190,
      render: (row, update) => {
        // Pick a standard tubular from Oxbo-M; the picker key resolves to an OD.
        // We store the OD (casing_size) — the numeric the nesting rule needs; the
        // picker value re-derives from the OD so the current OD stays selected.
        const currentKey =
          tubularEntries.find((e) => (e.meta?.["od"] as number | undefined) === row.casing_size)
            ?.key ?? null;
        return (
          <div className="cs-od">
            <CatalogPicker
              categoryKey="tubulars"
              value={currentKey}
              onChange={(key) => update({ casing_size: odForTubular(key) })}
              placeholder={t("casing.od.placeholder")}
            />
          </div>
        );
      },
    },
    {
      key: "top_installation",
      label: t("casing.col.top"),
      numeric: true,
      render: (row, update) => (
        <input
          className="cs-cell num tab-nums"
          inputMode="decimal"
          value={row.top_installation ?? ""}
          aria-label={t("casing.col.top")}
          onChange={(e) =>
            update({ top_installation: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "base_installation_depth",
      label: t("casing.col.base"),
      numeric: true,
      render: (row, update) => (
        <input
          className="cs-cell num tab-nums"
          inputMode="decimal"
          value={row.base_installation_depth ?? ""}
          aria-label={t("casing.col.base")}
          onChange={(e) =>
            update({
              base_installation_depth: e.target.value === "" ? null : Number(e.target.value),
            })
          }
        />
      ),
    },
    {
      key: "installation_length",
      label: t("casing.col.length"),
      numeric: true,
      render: (row) => (
        <input
          className="cs-cell num tab-nums"
          readOnly
          tabIndex={-1}
          aria-label={t("casing.col.length")}
          value={computedLength(row) ?? ""}
        />
      ),
    },
    {
      key: "nesting",
      label: t("casing.col.nesting"),
      width: 120,
      render: (_row, _update, i) => {
        const code = (nesting.byRow[i] ?? [])[0];
        if (!code) return <span className="badge closed">{t("casing.ok")}</span>;
        return (
          <span className="cs-warn-flag" title={t(`casing.warn.${code}`)}>
            <span className="ic ic-hse" aria-hidden="true" />
            {t(`casing.warn.${code}`)}
          </span>
        );
      },
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="screen-head">
        <h1>{t("casing.title")}</h1>
        <span className="badge info">{t("casing.badge")}</span>
        <div className="actions">
          <button type="button" className="btn" disabled>
            <span className="ic ic-import" aria-hidden="true" /> {t("casing.importHole")}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={!planId || !dirty || saving || !casingApplies}
          >
            <span className="ic ic-check" aria-hidden="true" />
            {saving ? t("casing.saving") : t("casing.save")}
          </button>
        </div>
      </div>

      {/* plan context chips */}
      {resolvedPlan && (
        <div className="cs-ctx">
          <span className="chip-ctx">
            <span className="k">{t("casing.ctx.well")}</span>
            <span className="mono">{resolvedPlan.well_uwi}</span>
            <span className="dim">·</span>
            <span className="k">{t("casing.ctx.event")}</span>
            <span className="mono">
              {enrich.letter ?? "?"} · {resolvedPlan.event_code}
            </span>
            <span className="dim">·</span>
            <span className="k">{t("casing.ctx.afe")}</span>
            <span className="mono">{resolvedPlan.afe_no ?? t("common.na")}</span>
          </span>
          <span className={`cs-status-pill ${statusClass(resolvedPlan.status)}`}>
            ● {t(STATUS_KEY[resolvedPlan.status], { n: resolvedPlan.version })}
          </span>
          <span className="cs-verchip">v{resolvedPlan.version}</span>
        </div>
      )}

      <QueryState
        isLoading={plansLoading || dsLoading}
        isError={dsError}
        isEmpty={!plansLoading && !dsLoading && !planId}
        emptyMessage={t("casing.noPlan")}
        onRetry={() => void refetch()}
      >
        {!casingApplies ? (
          <div className="cs-na">
            <h3>{t("casing.na.title")}</h3>
            <p>{t("casing.na.body", { code: resolvedPlan?.event_code ?? "" })}</p>
            <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
              <button type="button" className="btn sm" onClick={() => navigate("/program")}>
                {t("casing.na.toProgram")}
              </button>
              <button type="button" className="btn sm" onClick={() => navigate("/cost")}>
                {t("casing.na.toCost")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="cs-note">
              <span className="cs-qm">i</span>
              <span>{t("casing.note")}</span>
            </div>

            {/* KPI chips */}
            <div className="kpis">
              <div className="kpi">
                <div className="v tab-nums">{totals.count}</div>
                <div className="l">{t("casing.kpi.strings")}</div>
              </div>
              <div className="kpi">
                <div className="v tab-nums">
                  {fmtNum(totals.deepestShoe, locale)}{" "}
                  <span style={{ fontSize: 12, color: "var(--dim)" }}>m</span>
                </div>
                <div className="l">{t("casing.kpi.deepestShoe")}</div>
              </div>
              <div className="kpi">
                <div className="v tab-nums">
                  {totals.productionOd != null ? `${totals.productionOd}` : t("common.na")}
                  {totals.productionOd != null && (
                    <span style={{ fontSize: 12, color: "var(--dim)" }}> in</span>
                  )}
                </div>
                <div className="l">{t("casing.kpi.productionOd")}</div>
              </div>
              <div className="kpi">
                <div className={`v tab-nums${totals.warnCount > 0 ? " warn" : ""}`}>
                  {totals.warnCount}
                </div>
                <div className="l">{t("casing.kpi.warnings")}</div>
              </div>
            </div>

            <div className="cs-split">
              {/* LEFT: editable casing-string grid */}
              <section className="card" style={{ padding: 16 }}>
                <div
                  className="rowflex"
                  style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}
                >
                  <h2 style={{ margin: 0 }}>{t("casing.gridTitle")}</h2>
                  {savedAt && !dirty && (
                    <span className="dim" style={{ fontSize: 12 }}>
                      {t("casing.savedAt", { time: savedAt })}
                    </span>
                  )}
                </div>
                <div className="dim" style={{ fontSize: 12, margin: "-2px 0 12px" }}>
                  {t("casing.gridHint")}
                </div>

                <InlineRowGrid
                  rows={rows}
                  columns={columns}
                  onChange={applyRows}
                  makeEmptyRow={() => makeEmptyRow(rows)}
                  addLabel={t("casing.addString")}
                  emptyLabel={t("casing.empty")}
                />

                {/* nesting warning strip (only when a row is flagged) */}
                {firstWarnIdx >= 0 && (
                  <div className="cs-nest-note is-warn" role="status">
                    <span className="cs-qm warn">!</span>
                    <span>
                      <strong>
                        {t("casing.nest.warnTitle", {
                          name: rows[firstWarnIdx]?.string_name || `#${firstWarnIdx + 1}`,
                        })}
                      </strong>{" "}
                      {t("casing.nest.warnBody")}
                    </span>
                  </div>
                )}
                <div className="cs-nest-note">
                  <span className="cs-qm">i</span>
                  <span>{t("casing.nest.rule")}</span>
                </div>
              </section>

              {/* RIGHT: concentric casing schematic + legend */}
              <aside className="card cs-schem-card">
                <CasingSchematic
                  rows={rows}
                  rowKey={rowKey}
                  nestingByRow={nesting.byRow}
                  selectedKey={selectedKey}
                  onSelect={setSelectedKey}
                  caption={t("casing.schematic")}
                  ariaLabel={t("casing.schematicAria")}
                />
                <div className="cs-legend">
                  {rows.map((r, i) => {
                    const key = rowKey(r, i);
                    const warn = (nesting.byRow[i]?.length ?? 0) > 0;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`row${key === selectedKey ? " on" : ""}${warn ? " warn" : ""}`}
                        onClick={() => setSelectedKey(key)}
                      >
                        <span
                          className="sw"
                          style={{ background: warn ? "var(--warn)" : `var(${typeVar(r.type)})` }}
                        />
                        <span className="nm">
                          {r.string_name || `#${i + 1}`}
                          {warn && " ⚠"}
                        </span>
                        <span className="dims tab-nums">
                          {r.casing_size != null ? `${r.casing_size}″` : "—"} ·{" "}
                          {fmtNum(r.top_installation, locale)}–
                          {fmtNum(r.base_installation_depth, locale)} m
                        </span>
                      </button>
                    );
                  })}
                  {rows.length === 0 && <span className="dim">{t("casing.empty")}</span>}
                </div>
              </aside>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}
